import React, { memo, useState } from 'react';
import { cn } from '@/shared/utils/cn';
import { InlineTooltip } from '@/modules/Tooltip';
import { LinearGraphicData, LinearGraphicColor } from '../../types';
import { getColorValue } from '../../utils/colors';

export interface LinearGraphicSegmentProps {
  /** Datos del segmento */
  data: LinearGraphicData;
  /** Índice del segmento */
  index: number;
  /** Ancho del segmento en porcentaje */
  width: number;
  /** Color semántico del segmento */
  color: LinearGraphicColor;
  /** Si es el primer segmento */
  isFirst: boolean;
  /** Si es el último segmento */
  isLast: boolean;
  /** Altura de la barra */
  height: number;
  /** Duración de la transición */
  transitionDuration: number;
  /** Si mostrar tooltip */
  showTooltip: boolean;
  /** Callback cuando se hace hover */
  onHover?: (data: LinearGraphicData, index: number) => void;
  /** Callback cuando se sale del hover */
  onLeave?: (data: LinearGraphicData, index: number) => void;
}

/**
 * Segmento individual de la gráfica lineal
 * Átomo que representa una porción de la barra de progreso
 * Usa un tooltip CSS puro para evitar interferencias con el flex layout
 */
export const LinearGraphicSegment = memo(
  ({
    data,
    index,
    width,
    color,
    isFirst,
    isLast,
    height,
    transitionDuration,
    showTooltip,
    onHover,
    onLeave,
  }: LinearGraphicSegmentProps) => {
    const [isHovered, setIsHovered] = useState(false);

    // Validar que el ancho sea válido
    const validWidth = Math.max(0, Math.min(100, width));

    // Determinar las clases de border-radius
    const borderRadiusClasses = cn(
      isFirst && 'ui-rounded-l-control-pill',
      isLast && 'ui-rounded-r-control-pill',
      !isFirst && !isLast && 'ui-rounded-none'
    );

    // Obtener el valor CSS del color semántico
    const colorValue = getColorValue(color);

    // Estilos inline para el ancho, altura y color
    const segmentStyle: React.CSSProperties = {
      flex: `0 0 ${validWidth}%`,
      width: `${validWidth}%`,
      minWidth: validWidth > 0 && validWidth < 0.5 ? '1px' : undefined,
      height: `${height}px`,
      maxHeight: `${height}px`,
      transition: `flex-basis ${transitionDuration}ms ease-in-out, width ${transitionDuration}ms ease-in-out`,
      backgroundColor: colorValue,
    };

    // Contenido del tooltip
    const tooltipLabel = `${data.name}: ${data.percent}%`;

    // Handler de eventos
    const handleMouseEnter = () => {
      setIsHovered(true);
      onHover?.(data, index);
    };

    const handleMouseLeave = () => {
      setIsHovered(false);
      onLeave?.(data, index);
    };

    return (
      <div
        className={cn('ui-relative ui-h-full ui-flex-shrink-0', borderRadiusClasses)}
        style={segmentStyle}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        role="progressbar"
        aria-valuenow={data.percent}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-label={tooltipLabel}
      >
        {/* Tooltip inline - sin interferir con el flex layout */}
        {showTooltip && (
          <InlineTooltip label={tooltipLabel} isVisible={isHovered} position="bottom" />
        )}
      </div>
    );
  }
);

LinearGraphicSegment.displayName = 'LinearGraphicSegment';
